// Google Analytics — injection du script gtag pour mesurer le trafic sur
// l'ensemble de la plateforme Zifek. Le code de suivi (Measurement ID) est
// configuré par le superadmin dans les paramètres ; il est stocké dans la
// table `zifek` (colonne `google_analytics_id`).

import { supabase } from '@/lib/supabase';

let gaId: string | null = null;
let injected = false;

/** Valide le format d'un identifiant Google Analytics (GA4, UA ou GT). */
export function isValidGaId(id: string): boolean {
  return /^(G-[A-Z0-9]+|UA-\d{4,}-\d{1,}|GT-[A-Z0-9]+)$/i.test((id || '').trim());
}

/** Injecte le script gtag.js et initialise la mesure (sans page_view auto). */
function injectGaScript(id: string): void {
  if (typeof document === 'undefined' || injected) return;
  if (document.getElementById('ga-gtag-base')) {
    injected = true;
    return;
  }

  const loader = document.createElement('script');
  loader.id = 'ga-gtag-base';
  loader.async = true;
  loader.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(loader);

  const inline = document.createElement('script');
  inline.id = 'ga-gtag-init';
  inline.innerHTML = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=gtag;gtag('js',new Date());gtag('config','${id}',{send_page_view:false});`;
  document.head.appendChild(inline);

  injected = true;
}

/**
 * Charge la configuration Google Analytics depuis le Backend et injecte le
 * script. Retourne true si un identifiant valide a été trouvé et chargé.
 */
export async function loadGoogleAnalytics(): Promise<boolean> {
  try {
    const { data } = await supabase
      .from('zifek')
      .select('google_analytics_id')
      .order('id', { ascending: true })
      .limit(1)
      .maybeSingle();
    const id = String(data?.google_analytics_id || '').trim();
    if (!id || !isValidGaId(id)) return false;
    gaId = id;
    injectGaScript(id);
    return true;
  } catch {
    return false;
  }
}

/** Envoie un événement `page_view` (utile pour les SPA sans rechargement). */
export function trackGooglePageView(path?: string): void {
  if (!gaId || typeof window === 'undefined') return;
  const w = window as Window & { gtag?: (...args: unknown[]) => void };
  if (typeof w.gtag !== 'function') return;
  try {
    w.gtag('event', 'page_view', {
      page_path: path || window.location.pathname + window.location.search,
      page_location: window.location.href,
    });
  } catch {
    /* suivi non bloquant */
  }
}